/**
 * The catalogue's header band (client, 2026-09-16: *"still not happy with our modernity and
 * creativity on the catalogue"*).
 *
 * The page used to open on a 18 px title sharing one flex row with a count, a sort select
 * and a search field — a toolbar, not a front door. This is the front door: a full-bleed
 * band on a faint brand gradient carrying four things and nothing else.
 *
 * * **The title**, at the size a page title is read at rather than the size a section head is.
 * * **What the catalogue holds**, from the server's own `facets.data_type` — "57 rasters ·
 *   21 vectors · 17 tables". The facets are computed over the *whole* catalogue
 *   (`DatasetsResponse.facets`), so this line keeps describing the catalogue while the grid
 *   under it narrows. That is the point: a subtitle that shrank with the filters would just
 *   be the `n of total` count a second time.
 * * **The search**, centred and 48 px tall. It is the one control a reader opens a catalogue
 *   to use, and it keeps its own `catalogue-search` test id — the field moved, the contract
 *   did not.
 * * **A "Browse by" row of quick chips** (`quickChips`), which toggle the *same* facet state
 *   the rail does: a chip and its checkbox are two views of one `CatalogueFilters`, never a
 *   second filter mechanism, so ticking either moves the other and the URL.
 *
 * The band takes no query of its own — the page has already read `/datasets` for the grid,
 * and a header that fetched again would be a second answer to disagree with the first.
 */
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import type { DatasetDataType, DatasetFacets } from '../../lib/api';
import { dataTypeIcon, quickChips, typeCounts, type QuickChip } from './catalogue';

export interface CatalogueHeroProps {
  /** The envelope's own facets, over the whole catalogue. Absent before the first answer. */
  facets: DatasetFacets | undefined;
  /** The catalogue's size, for the search field's placeholder. */
  total: number;
  /** The live search text — the URL is the source of truth, exactly as the grid reads it. */
  q: string;
  onQuery: (q: string) => void;
  /** Is this chip's facet value currently ticked (in the rail or here — one state)? */
  isOn: (chip: QuickChip) => boolean;
  onChip: (chip: QuickChip) => void;
}

export function CatalogueHero({ facets, total, q, onQuery, isOn, onChip }: CatalogueHeroProps) {
  const { t } = useTranslation();
  const counts = typeCounts(facets?.data_type);
  const chips = quickChips(facets);

  /** A type/classification chip is translated; a source chip is an organisation's own name. */
  const chipLabel = (chip: QuickChip) =>
    chip.label ?? t(`catalogue.chips.${chip.value}`);

  return (
    <header className="cat-hero" data-testid="cat-hero">
      <div className="cat-hero-in">
        <h2 className="cat-hero-title">{t('catalogue.title')}</h2>
        <p className="cat-hero-sub" data-testid="cat-hero-counts">
          {t('catalogue.hero.counts', {
            rasters: counts.raster,
            vectors: counts.vector,
            tables: counts.tabular,
          })}
        </p>

        <div className="cat-hero-search">
          <Icon name="search" size={17} className="cat-search-icon" />
          <input
            className="cat-search"
            data-testid="catalogue-search"
            type="search"
            aria-label={t('catalogue.searchPlaceholder', { count: total })}
            placeholder={t('catalogue.searchPlaceholder', { count: total })}
            value={q}
            onChange={(e) => onQuery(e.target.value)}
          />
        </div>

        {chips.length > 0 && (
          <div className="cat-chips">
            <span className="cat-chips-label">{t('catalogue.hero.browse')}</span>
            {chips.map((chip) => {
              const on = isOn(chip);
              return (
                <button
                  key={chip.key}
                  type="button"
                  // `src` is a hook, not a skin: below 560 the source chips are hidden and
                  // the row scrolls sideways rather than wrapping to three lines.
                  className={`cat-chip${chip.group === 'src' ? ' src' : ''}${on ? ' on' : ''}`}
                  data-testid={`cat-chip-${chip.key}`}
                  aria-pressed={on}
                  onClick={() => onChip(chip)}
                >
                  {/* A tick **in the glyph's own slot** once the chip is on (review,
                      2026-09-16): a 12 % fill alone is easy to miss across a row of seven,
                      and a check beside the group glyph would be two marks saying one thing
                      in a chip 100 px wide. The label still names what kind of chip it is,
                      so nothing is lost by the swap — and the slot is the same width either
                      way, so the row does not reflow as chips go on and off. */}
                  {on ? (
                    <Icon name="check" size={12} className="cchk" />
                  ) : chip.group === 'type' ? (
                    <Icon name={dataTypeIcon(chip.value as DatasetDataType)} size={13} />
                  ) : chip.group === 'cls' ? (
                    <Icon name="lock" size={13} />
                  ) : (
                    <Icon name="building" size={13} />
                  )}
                  <span className="cname">{chipLabel(chip)}</span>
                  <span className="ccount">{chip.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
}

export default CatalogueHero;
